// The Benchmark section of the Sentinel Workspace: golden cases to answer with form 1.6. The sidebar picks To do
// (in progress first, then not started), Done or, for editors, Monitoring; `&case=<id>` in the URL opens one case.

import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { GoldenAdapter } from '@/adapters/golden.adapter';
import { getApiErrorMessage } from '@/lib/utils';
import type { GoldenCaseRow, GoldenMe, GoldenTab } from '@/types/golden.types';
import { BenchmarkCase } from './benchmark-case';
import { BenchmarkProgress } from './benchmark-progress';
import { benchmarkDoneQueryAtom, benchmarkInProgressQueryAtom, benchmarkTodoQueryAtom, goldenMeQueryAtom } from './benchmark-state';

export const CASE_PARAM = 'case';
/** `&view=todo|done`; anything else is To do. Monitoring is a section of its own. */
export const VIEW_PARAM = 'view';
export type BenchmarkView = 'todo' | 'done' | 'monitoring';
// Radix Select has no empty value, so "all" is a value of its own.
const ALL = 'all';
const STATUS_LABELS: Record<GoldenTab, string> = { todo: 'To do', in_progress: 'In progress', done: 'Done' };
export const EMPTY_TODO = 'No cases waiting. New cases arrive every morning (UTC).';
export const EMPTY_DONE = 'No submitted cases yet.';

type Filters = { account: string; day: string };
const matches = (row: GoldenCaseRow, filters: Filters) =>
  (filters.account === ALL || String(row.accountId) === filters.account) && (filters.day === ALL || row.frameDay === filters.day);

function CaseTable({ rows, role, onOpen }: { rows: GoldenCaseRow[]; role: GoldenMe['role']; onOpen: (id: string) => void }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Brand</TableHead>
          <TableHead>Frame day</TableHead>
          <TableHead>Case</TableHead>
          <TableHead>Channel</TableHead>
          <TableHead>Position</TableHead>
          <TableHead>Received</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{row.brand}</TableCell>
            <TableCell className="tabular-nums">{row.frameDay}</TableCell>
            <TableCell>
              {/* Sentinels open To do cases only through Start next, in their fixed order; editors can open any case. */}
              {row.status === 'todo' && role !== 'editor' ? (
                row.shortId
              ) : (
                <Button variant="link" className="h-auto p-0 text-xs" onClick={() => onOpen(row.id)}>
                  {row.shortId}
                </Button>
              )}
            </TableCell>
            <TableCell>{row.channel}</TableCell>
            <TableCell>{row.position}</TableCell>
            <TableCell className="tabular-nums">{row.received}</TableCell>
            <TableCell>
              {row.status === 'in_progress' ? (
                <Button variant="link" className="h-auto p-0 text-xs" aria-label={`Continue ${row.shortId}`} onClick={() => onOpen(row.id)}>
                  Continue
                </Button>
              ) : (
                STATUS_LABELS[row.status]
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: [string, string][]; onChange: (value: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" aria-label={label} className="w-[160px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(([optionValue, optionLabel]) => (
          <SelectItem key={optionValue} value={optionValue}>
            {optionLabel}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BenchmarkList({ me, view, onOpen, viewSwitch }: { me: GoldenMe; view: 'todo' | 'done'; onOpen: (id: string) => void; viewSwitch: React.ReactNode }) {
  const [filters, setFilters] = useState<Filters>({ account: ALL, day: ALL });
  // To do = in progress first, then not started; Done = submitted. One list request per view.
  const list = useAtomValue(view === 'done' ? benchmarkDoneQueryAtom : benchmarkInProgressQueryAtom);
  const todo = useAtomValue(benchmarkTodoQueryAtom);
  const queries = view === 'done' ? [list] : [list, todo];
  const failed = queries.find((query) => query.isError);

  const allRows = useMemo(
    () => (view === 'done' ? (list.data?.rows ?? []) : [...(list.data?.rows ?? []), ...(todo.data?.rows ?? [])]),
    [view, list.data, todo.data],
  );
  const rows = useMemo(() => allRows.filter((row) => matches(row, filters)), [allRows, filters]);
  const days = useMemo(() => [...new Set(allRows.map((row) => row.frameDay))].sort().reverse(), [allRows]);
  const next = todo.data?.rows.find((row) => matches(row, filters));

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex flex-wrap items-center gap-3">
        {viewSwitch}
        <div className="ml-auto">
          <Button size="sm" disabled={!next} onClick={() => next && onOpen(next.id)}>
            Start next
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 empty:hidden">
        {me.accounts.length > 1 && (
          <FilterSelect
            label="Brand"
            value={filters.account}
            onChange={(account) => setFilters({ ...filters, account })}
            options={[[ALL, 'All brands'], ...me.accounts.map((id): [string, string] => [String(id), GoldenAdapter.brandName(id)])]}
          />
        )}
        {/* Kept while a day is chosen, so a view without that day can still clear it. */}
        {(days.length > 1 || filters.day !== ALL) && (
          <FilterSelect
            label="Frame day"
            value={filters.day}
            onChange={(day) => setFilters({ ...filters, day })}
            options={[[ALL, 'All days'], ...days.map((day): [string, string] => [day, day])]}
          />
        )}
      </div>
      {failed ? (
        <p role="alert" className="text-[13px] text-red-600">
          {getApiErrorMessage(failed.error, 'Could not load the cases.')}
        </p>
      ) : queries.some((query) => query.isPending) ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-[13px] text-gray-800">{view === 'todo' ? EMPTY_TODO : EMPTY_DONE}</p>
      ) : (
        <CaseTable rows={rows} role={me.role} onOpen={onOpen} />
      )}
    </div>
  );
}

/** `viewSwitch`: the page title (the sidebar is the way to the other views). */
export function BenchmarkPanel({ view, viewSwitch }: { view: BenchmarkView; viewSwitch: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useAtomValue(goldenMeQueryAtom);
  const caseId = searchParams.get(CASE_PARAM);
  const openCase = (id: string | null) =>
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      if (id) params.set(CASE_PARAM, id);
      else params.delete(CASE_PARAM);
      return params;
    });

  if (me.isPending) return <Skeleton className="m-4 h-40 w-full rounded-lg" />;
  if (me.isError) {
    return (
      <div className="flex w-full flex-col items-start gap-3 p-4">
        {viewSwitch}
        <p role="alert" className="text-[13px] text-red-600">
          {getApiErrorMessage(me.error, 'Could not check your access to Benchmark.')}
        </p>
        <Button variant="outline" size="sm" onClick={() => void me.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (me.data.role === 'none') {
    return (
      <div className="flex w-full flex-col gap-4 p-4">
        {viewSwitch}
        <p className="text-[13px] text-gray-800">Benchmark is only for the Sentinels of its brands.</p>
      </div>
    );
  }
  if (view === 'monitoring') {
    return (
      <div className="flex h-full w-full min-w-0 flex-col gap-4 overflow-y-auto p-4">
        {viewSwitch}
        {me.data.role === 'editor' ? (
          <BenchmarkProgress />
        ) : (
          <p className="text-[13px] text-gray-800">Monitoring is only for Benchmark admins.</p>
        )}
      </div>
    );
  }
  if (caseId) return <BenchmarkCase key={caseId} caseId={caseId} me={me.data} onOpen={openCase} viewSwitch={viewSwitch} />;
  return <BenchmarkList me={me.data} view={view} onOpen={openCase} viewSwitch={viewSwitch} />;
}
